import { useCallback, useEffect, useMemo, useState } from "react";
import { handlePromise } from "../../../utils/handlePromisse";
import { Patient, getPatientsPaginated2, deletePatient } from "../../../services/patient.service";
import { savePatientData } from "./form/save-patient";
import { filterPatients, PatientSortOrder, sortPatients } from "./patients-filters";

const MOBILE_PAGE_SIZE = 10;
export function usePatients() {
  const [allPatients, setAllPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<PatientSortOrder>("name-asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [mobileVisible, setMobileVisible] = useState(MOBILE_PAGE_SIZE);

  const [openForm, setOpenForm] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>(undefined);

  const [openDialog, setOpenDialog] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<number | undefined>(undefined);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    const [result, error] = await handlePromise(getPatientsPaginated2({ page: 1, size: 1000 }));
    setLoading(false);
    if (!!error) {
      setShowSnackbar(true);
      setSnackbarMessage(error.response?.data?.message || "Erro ao carregar pacientes");
      return;
    }
    setAllPatients(result.data.data as Patient[]);
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    allPatients.forEach((p) => {
      if (Array.isArray(p.tags)) p.tags.forEach((t) => set.add(t));
    });
    return Array.from(set);
  }, [allPatients]);

  const filtered = useMemo(() => filterPatients(allPatients, query), [allPatients, query]);
  const sorted = useMemo(() => sortPatients(filtered, sortOrder), [filtered, sortOrder]);
  const pageRows = useMemo(() => sorted.slice((page - 1) * pageSize, page * pageSize), [sorted, page, pageSize]);
  const mobileSlice = useMemo(() => sorted.slice(0, mobileVisible), [sorted, mobileVisible]);

  useEffect(() => {
    setPage(1);
    setMobileVisible(MOBILE_PAGE_SIZE);
  }, [query]);

  const handleSavePatient = useCallback(
    async (patient: Patient & { photos?: File[]; removedImageIds?: number[] }) => {
      const isNewPatient = !patient.id || patient.id === 0;
      setShowSnackbar(false);
      setLoading(true);
      const [, error] = await savePatientData(patient);
      setLoading(false);
      if (!!error) {
        setShowSnackbar(true);
        setSnackbarMessage(error.response?.data?.message || "Erro ao salvar paciente");
        return;
      }
      setShowSnackbar(true);
      setSnackbarMessage(isNewPatient ? "Paciente criado com sucesso!" : "Paciente atualizado com sucesso!");
      setOpenForm(false);
      setSelectedPatient(undefined);
      fetchPatients();
    },
    [fetchPatients]
  );

  const handleDeletePatient = useCallback(async () => {
    setLoading(true);
    const [, error] = await handlePromise(deletePatient(patientToDelete || -1));
    setLoading(false);
    if (!!error) {
      setShowSnackbar(true);
      setSnackbarMessage(error.response?.data?.message || "Erro ao deletar paciente");
      return;
    }
    setOpenDialog(false);
    setShowSnackbar(true);
    setSnackbarMessage("Paciente deletado com sucesso!");
    setPatientToDelete(undefined);
    fetchPatients();
  }, [patientToDelete, fetchPatients]);

  const handleDelete = useCallback((patientId: number) => {
    setPatientToDelete(patientId);
    setOpenDialog(true);
  }, []);

  const handleAddNew = useCallback(() => {
    setShowSnackbar(false);
    setSelectedPatient(undefined);
    setOpenForm(true);
  }, []);

  const closeForm = useCallback(() => {
    setOpenForm(false);
    setSelectedPatient(undefined);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setOpenDialog(false);
    setPatientToDelete(undefined);
  }, []);

  return {
    loading,
    query,
    setQuery,
    page,
    setPage,
    pageSize,
    setPageSize,
    mobileVisible,
    setMobileVisible,
    mobilePageSize: MOBILE_PAGE_SIZE,
    allPatients,
    allTags,
    filtered,
    sorted,
    sortOrder,
    setSortOrder,
    pageRows,
    mobileSlice,
    openForm,
    selectedPatient,
    openDialog,
    showSnackbar,
    snackbarMessage,
    setShowSnackbar,
    handleSavePatient,
    handleDeletePatient,
    handleDelete,
    handleAddNew,
    closeForm,
    closeDeleteDialog,
  };
}
